// Web Push (PWA) subscription helpers for Quantora
import api from './api';

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export async function getPushPermission() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function enablePush() {
  if (!pushSupported()) throw new Error('Push not supported on this browser.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied.');
  }

  const reg = await navigator.serviceWorker.ready;

  // Fetch VAPID public key
  const { data } = await api.get('/push/vapid-public-key');
  const vapidKey = data?.publicKey;
  if (!vapidKey) throw new Error('Server has no VAPID key configured.');

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  // Send subscription to backend
  const subJson = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    expirationTime: subJson.expirationTime,
  });
  return true;
}

export async function disablePush() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return true;
  try {
    const subJson = subscription.toJSON();
    await api.post('/push/unsubscribe', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    });
  } catch (_) { /* best effort */ }
  try { await subscription.unsubscribe(); } catch (_) { /* ignore */ }
  return true;
}

export async function pushStatus() {
  if (!pushSupported()) return { subscribed: false, device_count: 0, permission: 'unsupported' };
  const perm = Notification.permission;
  try {
    const { data } = await api.get('/push/status');
    return { ...data, permission: perm };
  } catch (_) {
    return { subscribed: false, device_count: 0, permission: perm };
  }
}

export async function sendTestPush() {
  const { data } = await api.post('/push/test');
  return data;
}
